/**
 * MapView — Leaflet/OpenStreetMap wrapper used by the dispatch screen.
 * No API key required. Pins are colour-coded by job status.
 */
import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Leaflet's default icons break under Webpack; provide an explicit set.
const makeIcon = (color) =>
  L.divIcon({
    className: 'leaflet-div-icon-clear',
    html: `
      <div style="position:relative; width:32px; height:42px;">
        <svg width="32" height="42" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg">
          <path d="M16 0C7.16 0 0 7.16 0 16c0 12 16 26 16 26s16-14 16-26C32 7.16 24.84 0 16 0z"
                fill="${color}" stroke="white" stroke-width="2"/>
          <circle cx="16" cy="16" r="6" fill="white"/>
        </svg>
      </div>`,
    iconSize: [32, 42],
    iconAnchor: [16, 42],
    popupAnchor: [0, -36],
  });

const STATUS_COLOR = {
  pending: '#f59e0b',     // amber
  in_progress: '#a855f7', // violet
  active_call: '#06b6d4', // cyan
  completed: '#10b981',   // emerald
  urgent: '#ef4444',      // red
};

function FitToBounds() { return null; /* legacy - no longer used */ }

export default function MapView({
  pins = [],
  center = { lat: 39.5, lng: -98.35, zoom: 4 },
  height = 480,
  onPinClick,
}) {
  // React 18 StrictMode double-mounts components in dev, which makes
  // react-leaflet throw "Map container is already initialized". The reliable
  // fix is to use an imperative Leaflet map instance bound to our own div,
  // and properly tear it down on unmount.
  const mapDivRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersLayerRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const [_, force] = useState(0);

  useEffect(() => {
    if (!mapDivRef.current || mapInstanceRef.current) return;
    const map = L.map(mapDivRef.current, {
      center: [center.lat, center.lng],
      zoom: center.zoom || 5,
      scrollWheelZoom: true,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);
    markersLayerRef.current = L.layerGroup().addTo(map);
    mapInstanceRef.current = map;
    force((n) => n + 1);
    return () => {
      try { map.remove(); } catch (_) { /* noop */ }
      mapInstanceRef.current = null;
      markersLayerRef.current = null;
    };
  }, [center.lat, center.lng, center.zoom]);

  // Update pins whenever they change
  useEffect(() => {
    const layer = markersLayerRef.current;
    const map = mapInstanceRef.current;
    if (!layer || !map) return;
    layer.clearLayers();
    if (pins.length === 0) {
      map.setView([center.lat, center.lng], center.zoom || 4);
      return;
    }
    pins.forEach((p) => {
      const color =
        p.priority === 'urgent'
          ? STATUS_COLOR.urgent
          : STATUS_COLOR[p.status] || '#0ea5e9';
      const m = L.marker([p.lat, p.lng], { icon: makeIcon(color) });
      m.bindPopup(
        `<div style="min-width:180px;">
          <p style="font-weight:700;font-size:13px;margin:0 0 4px;">${escapeHtml(p.title || 'Job')}</p>
          ${p.customer_name ? `<p style="font-size:11px;margin:0;">${escapeHtml(p.customer_name)}</p>` : ''}
          ${p.address ? `<p style="font-size:11px;color:#64748b;margin:4px 0 0;">${escapeHtml(p.address)}</p>` : ''}
          <div style="font-size:10px;margin-top:8px;display:inline-block;padding:2px 8px;border-radius:4px;font-weight:600;text-transform:uppercase;background:${color}22;color:${color};">
            ${p.priority === 'urgent' ? 'URGENT' : (p.status || '').replace('_', ' ')}
          </div>
        </div>`
      );
      if (onPinClick) m.on('click', () => onPinClick(p));
      m.addTo(layer);
    });
    if (pins.length === 1) {
      map.setView([pins[0].lat, pins[0].lng], 13);
    } else {
      const bounds = L.latLngBounds(pins.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [pins, center.lat, center.lng, center.zoom, onPinClick]);

  return (
    <div
      className="rounded-2xl overflow-hidden border border-border/60 shadow-sm"
      style={{ height }}
      data-testid="map-view"
    >
      <div ref={mapDivRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}
